import { Injectable, NgZone } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable, Subject, firstValueFrom } from 'rxjs';
import {
  HubConnection,
  HubConnectionBuilder,
  LogLevel,
} from '@microsoft/signalr';
import { environment } from '../../environments/environment';
import { LiveTickSequencer } from './live-tick-sequencer';
import {
  TelemetryFamilyName,
  isKnownFamily,
} from './telemetry-conversion';

/**
 * Live tick stream (telemetry-pipeline item 4), wired to the confirmed
 * DataServices contract (c7db19e).
 *
 *   negotiate  POST {apiUrl}live/negotiate  (Bearer)  -> { url, accessToken }
 *   hub        telemetry
 *   event      'tick'
 *   payload    COALESCED ENVELOPE, one per broadcast:
 *              { project, timestamp, families: [
 *                  { family: 'TEMPERATURE'|'ZONESTORAGE'|'LOSS',
 *                    data: [<raw ushort>...], timestamp?, seq?, hwm? }, ... ] }
 *
 *              Older single-family shape, still tolerated:
 *              { project, family, timestamp, data: [...] }
 *
 * COALESCING MOVED THE PAYLOAD. Since LivePushCoalesce, all three families live
 * ONLY in `families`; the top-level family/data pair carries just one of them
 * (LOSS). A client reading the old shape therefore ingests ONE family and
 * silently loses the other two — observed as loss and zone frozen while the
 * socket reported `connected`, with the poll fallback suppressed BY DESIGN
 * because the socket was live. Read `families` when present and fan it out.
 *
 * Three things about that contract shape the design:
 *
 * 1. `data` is RAW ushort at full resolution — not the converted floats the REST
 *    backfill returns. Conversion happens client-side (see telemetry-conversion),
 *    and viewport decimation is re-derived exactly as the REST path already is.
 * 2. The broadcast is GLOBAL: every client receives every tick for every project.
 *    Filtering on `project` is therefore mandatory for correctness, not an
 *    optimisation — without it one project's chart would ingest another's ticks.
 * 3. Ordering is ascending per FAMILY, not across families. A LOSS tick may carry
 *    a lower timestamp than the newest TEMPERATURE tick, so the ordering gate is
 *    per family; a single shared gate would silently drop valid ticks.
 *
 * Polling remains the source of truth: live is skipped entirely when the flag is
 * off, and polling resumes the moment the socket is unavailable or drops.
 *
 * Auth: BEARER ONLY. negotiate carries the VeriCure API token via the
 * socket authenticates with the returned accessToken. No Entra bearer is involved,
 * so Google and email/password users are included.
 */

/**
 * One family's rows inside a coalesced envelope.
 *
 * Captured from the wire: `{ ds, data }` and nothing else. The entries carry NO
 * timestamp, seq or hwm — every frame in an envelope shares the envelope's.
 */
export interface LiveFamilyFrame {
  /** Downsample marker, as sent. Not used for ordering. */
  ds?: number;
  data?: number[];
  /** Only on the legacy array shape, where each entry named itself. */
  family?: string;
  timestamp?: number;
}

/** A tick exactly as broadcast. */
export interface LiveTickMessage {
  /**
   * The coalesced payload, KEYED BY FAMILY NAME:
   *
   *     families: { LOSS: {ds, data}, TEMPERATURE: {ds, data}, ... }
   *
   * An ARRAY is also accepted, purely so a contract change in that direction
   * cannot blind the client again — but the object is what the server sends.
   *
   * When present this is the ONLY place the families live. The top-level
   * family/data pair repeats the ORDINAL-FIRST family (always LOSS), so reading
   * it instead loses every other family on the frame, silently.
   */
  families?: Record<string, LiveFamilyFrame> | LiveFamilyFrame[];
  project?: string;
  family?: string;
  timestamp?: number;
  data?: number[];
  /**
   * Monotonic per-family sequence number, when the server sends one.
   *
   * Optional on purpose: without it a dropped frame is invisible, because
   * timestamps alone cannot distinguish "no tick was produced" from "a tick was
   * produced and lost". With it, a break in the run triggers ONE catch-up poll
   * instead of a silent hole in the chart.
   */
  seq?: number;
  /** Server high-water mark for this family, if sent. Seeds the poll cursor. */
  hwm?: number;
}

/** A validated, family-tagged tick ready for conversion. */
export interface LiveTick {
  project: string;
  family: TelemetryFamilyName;
  timestamp: number;
  data: number[];
  seq?: number;
  hwm?: number;
  /** Alias so the ordering gate can read the epoch generically. */
  E: number;
}

export type LiveConnectionState =
  | 'disabled'
  | 'connecting'
  | 'connected'
  | 'reconnecting'
  | 'disconnected'
  /**
   * Tried enough times, staying on polling. TERMINAL for this project: nothing
   * retries out of it, so a permanently broken hub stops generating load.
   * Cleared by a project change or an explicit retryNow().
   */
  | 'unavailable';

/**
 * Backoff for the CONNECT attempt.
 *
 * Not to be confused with `withAutomaticReconnect`, which only governs drops
 * AFTER a successful start — a start() that throws never reaches it. The retry
 * that mattered lived in the caller's 5 s poll timer, which re-tried a settled
 * 'disconnected' every tick, forever: two HTTP round trips every 5 s per open
 * tab, one of them our function app, for as long as the hub stayed broken.
 * Measured at ~25 service negotiates and ~10 first-party negotiates in two
 * minutes against a hub that was never going to answer.
 *
 * Delays are the standard ladder, then a ceiling. The cap matters more than the
 * curve: the failure this is sized for is a misconfigured service that will not
 * fix itself, so the steady state has to be cheap, not merely slower.
 */
const CONNECT_BACKOFF_MS = [0, 2_000, 10_000, 30_000, 60_000] as const;
const CONNECT_BACKOFF_CEILING_MS = 300_000;
/** Attempts before giving up entirely and saying so. */
const CONNECT_MAX_ATTEMPTS = 8;

/** Client half of the server's LiveTelemetryEnabled switch. Absent config = OFF. */
export function liveTelemetryEnabled(): boolean {
  return (
    (environment as { liveTelemetryEnabled?: boolean }).liveTelemetryEnabled === true
  );
}

interface LiveTelemetryConfig {
  negotiateRoute: string;
  /**
   * Per-project subscription routes (DS's security cutover).
   *
   * HTTP, not hub methods: the server needs a validated bearer to resolve the
   * caller's company before it will bind a connection to a project, and that
   * validation lives on the function route rather than on the hub.
   */
  subscribeRoute: string;
  unsubscribeRoute: string;
  hubName: string;
  tickEvent: string;
}

/** Confirmed DataServices contract (c7db19e). */
export const DEFAULT_LIVE_CONFIG: LiveTelemetryConfig = {
  negotiateRoute: 'live/negotiate',
  subscribeRoute: 'live/subscribe',
  unsubscribeRoute: 'live/unsubscribe',
  hubName: 'telemetry',
  tickEvent: 'tick',
};

export function liveTelemetryConfig(): LiveTelemetryConfig {
  const configured = (
    environment as { liveTelemetry?: Partial<LiveTelemetryConfig> }
  ).liveTelemetry;
  return { ...DEFAULT_LIVE_CONFIG, ...(configured ?? {}) };
}

/** Negotiate response. */
export interface NegotiateResponse {
  url: string;
  accessToken: string;
}

@Injectable({ providedIn: 'root' })
export class LiveTelemetryService {
  private connection: HubConnection | null = null;
  private projectName = '';
  /** SignalR connection id, required by the subscribe/unsubscribe routes. */
  private connectionId = '';

  /**
   * The project a connect is CURRENTLY being established for.
   *
   * Claimed synchronously so a second caller cannot slip through while the first
   * is awaiting negotiate. Without it, start() is only idempotent once the
   * socket already exists — which is exactly when idempotence is not needed.
   */
  private startingProject: string | null = null;

  /**
   * One ordering gate PER FAMILY — timestamps are ascending within a family, not
   * across them.
   */
  private sequencers: Record<TelemetryFamilyName, LiveTickSequencer<LiveTick>> = {
    TEMPERATURE: new LiveTickSequencer<LiveTick>(),
    ZONESTORAGE: new LiveTickSequencer<LiveTick>(),
    LOSS: new LiveTickSequencer<LiveTick>(),
  };

  /**
   * The project the backoff below belongs to.
   *
   * Deliberately NOT `projectName`: startInternal() calls stop() first, and stop()
   * clears projectName, so keying the backoff on it made every retry look like a
   * new project — the counter reset to 0 each time and the give-up was
   * unreachable. The failing test is what found that; the code read as correct.
   */
  private backoffProject = '';
  /** Consecutive failed connect attempts for backoffProject. */
  private connectFailures = 0;
  /** Epoch ms before which another connect attempt is refused. */
  private nextAttemptAt = 0;

  private readonly stateSource = new BehaviorSubject<LiveConnectionState>('disabled');
  readonly state$: Observable<LiveConnectionState> = this.stateSource.asObservable();

  private readonly ticksSource = new Subject<LiveTick[]>();
  /** Ordered, de-duplicated, project- and family-filtered ticks. */
  readonly ticks$: Observable<LiveTick[]> = this.ticksSource.asObservable();

  private readonly catchUpSource = new Subject<string>();
  /**
   * Fires on connect, reconnect and drop. The caller answers with one poll per
   * family from its cursor, which is gap-free (item 2 HWM).
   */
  readonly catchUpNeeded$: Observable<string> = this.catchUpSource.asObservable();

  constructor(private zone: NgZone, private http: HttpClient) {}

  get state(): LiveConnectionState {
    return this.stateSource.value;
  }

  /**
   * Clear the backoff and the give-up so the next start() actually tries.
   *
   * The escape hatch for "the hub was fixed while I had the tab open". Without
   * it, 'unavailable' would outlive the outage that caused it and only a reload
   * would recover.
   */
  retryNow(): void {
    this.backoffProject = '';
    this.connectFailures = 0;
    this.nextAttemptAt = 0;
    if (this.stateSource.value === 'unavailable') {
      this.setState('disconnected');
    }
  }

  /** True only when live is enabled AND the socket is actually up. */
  get isLive(): boolean {
    return liveTelemetryEnabled() && this.stateSource.value === 'connected';
  }

  /**
   * POST the negotiate endpoint.
   *
   * BEARER ONLY. The URL is built from environment.apiUrl, which is the prefix
   * registered in protectedResourceMap, and the request goes through Angular's
   * HttpClient — so VericureAuthInterceptor attaches the VeriCure API token
   * exactly as it does for REST. The function key that used to ride alongside is
   * gone: it is no longer in the bundle, and the server rejects it.
   *
   * The project name is sent in the body so the hub can authorise the caller for
   * THIS project and resolve its group server-side, rather than the client
   * asserting a group key of its own choosing.
   */
  async negotiate(projectName: string): Promise<NegotiateResponse> {
    const cfg = liveTelemetryConfig();
    const base = environment.apiUrl.endsWith('/')
      ? environment.apiUrl
      : `${environment.apiUrl}/`;
    const route = cfg.negotiateRoute.replace(/^\/+/, '');
    const url = `${base}${route}`;
    return firstValueFrom(
      this.http.post<NegotiateResponse>(url, { projectName })
    );
  }

  /**
   * Connect for a project. No-op when the flag is off — callers simply keep
   * polling, which is the pre-item-4 behaviour.
   *
   * @param seed per-family epochs already ingested, so replayed ticks are dropped.
   */
  async start(
    projectName: string,
    seed: Partial<Record<TelemetryFamilyName, number>> = {}
  ): Promise<void> {
    if (!liveTelemetryEnabled() || !projectName) {
      this.setState('disabled');
      return;
    }
    // ONE CONNECTION, HELD.
    //
    // The early return below only saw `connection`, which is assigned AFTER
    // `stop()` and `negotiate()` have both been awaited. Every caller arriving
    // inside that window saw null and built its own socket — ~62 sockets in 45
    // seconds against a hub that needs exactly one. The claim has to be staked
    // SYNCHRONOUSLY, before the first await, or it does not prevent anything.
    if (this.startingProject === projectName) {
      return;
    }
    if (this.connection && this.projectName === projectName) {
      return;
    }

    // A NEW PROJECT IS A NEW QUESTION. Its hub may be fine even if the last
    // one's was not, so the backoff and the give-up both clear here.
    if (projectName !== this.backoffProject) {
      this.backoffProject = projectName;
      this.connectFailures = 0;
      this.nextAttemptAt = 0;
      if (this.stateSource.value === 'unavailable') {
        this.setState('disconnected');
      }
    }

    // Terminal: stop asking. Callers keep polling, which is the fallback either
    // way, so refusing here costs nothing but the load.
    if (this.stateSource.value === 'unavailable') {
      return;
    }
    if (Date.now() < this.nextAttemptAt) {
      return;
    }
    this.startingProject = projectName;

    try {
      await this.startInternal(projectName, seed);
    } finally {
      this.startingProject = null;
    }
  }

  /** The body of start(), serialised by the claim above. */
  private async startInternal(
    projectName: string,
    seed: Partial<Record<TelemetryFamilyName, number>>
  ): Promise<void> {
    await this.stop();

    this.projectName = projectName;
    this.resetSequencers(seed);

    const cfg = liveTelemetryConfig();
    try {
      this.setState('connecting');
      const negotiated = await this.negotiate(projectName);
      if (!negotiated?.url || !negotiated?.accessToken) {
        throw new Error('negotiate returned no url/accessToken');
      }

      const connection = new HubConnectionBuilder()
        .withUrl(negotiated.url, {
          // Only negotiate needs the function key; the socket uses this token.
          accessTokenFactory: () => negotiated.accessToken,
        })
        .withAutomaticReconnect([0, 2000, 5000, 10000, 30000])
        .configureLogging(LogLevel.Warning)
        .build();

      connection.on(cfg.tickEvent, (message: LiveTickMessage) =>
        this.onTick(message)
      );

      connection.onreconnecting(() => {
        this.setState('reconnecting');
        this.catchUpSource.next('signalr reconnecting');
      });
      connection.onreconnected(() => {
        this.setState('connected');
        this.catchUpSource.next('signalr reconnected');
        // A RECONNECT ISSUES A NEW CONNECTION ID, and the server's binding is to
        // the OLD one. Without re-subscribing the socket comes back up and stays
        // silent for the rest of the session — the failure mode is a chart that
        // simply stops advancing, with a connection that looks healthy.
        const id = connection.connectionId ?? '';
        this.connectionId = id;
        if (this.projectName && id) {
          void this.subscribe(this.projectName, id).then((ok) => {
            if (!ok) {
              console.warn(
                'LiveTelemetry: re-subscribe after reconnect failed — polling'
              );
            }
          });
        }
      });
      connection.onclose(() => {
        this.setState('disconnected');
        this.catchUpSource.next('signalr closed');
      });

      this.connection = connection;
      await connection.start();

      // SUBSCRIBE THIS CONNECTION TO THIS PROJECT.
      //
      // Nothing arrives until this succeeds — the server no longer broadcasts to
      // every connection, which is the point of the cutover. The connectionId
      // comes from the started connection, so this cannot run before start().
      //
      // A failure here is NOT survivable in the way the old group-join was: there
      // is no broadcast to fall back on, so the socket would sit connected and
      // silent. It therefore falls back to polling explicitly and says why.
      this.connectionId = connection.connectionId ?? '';
      const subscribed = await this.subscribe(projectName, this.connectionId);
      if (!subscribed) {
        console.warn(
          'LiveTelemetry: subscribe failed — no frames will arrive on this ' +
            'socket. Falling back to polling.'
        );
        this.catchUpSource.next('subscribe failed');
      }

      this.connectFailures = 0;
      this.nextAttemptAt = 0;
      this.setState('connected');
      this.catchUpSource.next('signalr connected');
    } catch (error) {
      this.connection = null;
      this.connectFailures += 1;

      if (this.connectFailures >= CONNECT_MAX_ATTEMPTS) {
        console.warn(
          `LiveTelemetry: giving up after ${this.connectFailures} attempts — ` +
            'staying on polling for this project.',
          error
        );
        this.nextAttemptAt = Number.POSITIVE_INFINITY;
        this.setState('unavailable');
      } else {
        const idx = Math.min(this.connectFailures - 1, CONNECT_BACKOFF_MS.length - 1);
        const delay = Math.min(CONNECT_BACKOFF_MS[idx], CONNECT_BACKOFF_CEILING_MS);
        this.nextAttemptAt = Date.now() + delay;
        console.warn(
          `LiveTelemetry: connect failed (${this.connectFailures}/${CONNECT_MAX_ATTEMPTS}), ` +
            `staying on polling; next attempt in ${Math.round(delay / 1000)}s`,
          error
        );
        this.setState('disconnected');
      }
      this.catchUpSource.next('signalr connect failed');
    }
  }

  /** Disconnect. The gates hold no buffer, so there is nothing to drain. */
  async stop(): Promise<void> {
    const connection = this.connection;
    const leaving = this.projectName;
    const connectionId = this.connectionId;
    this.connection = null;
    this.projectName = '';
    this.connectionId = '';
    if (connection) {
      // Leave before dropping the socket. Closing the connection releases the
      // group anyway, but a project SWITCH reuses this path and an explicit
      // leave is what makes the server's view unambiguous if it ever pools
      // connections.
      if (leaving && connectionId) {
        // Release the binding before the socket goes. A project SWITCH reuses
        // this path, so without it the server would keep the previous project
        // bound to a connection id that is about to be reused or dropped.
        await this.unsubscribe(leaving, connectionId);
      }
      try {
        await connection.stop();
      } catch {
        // Already closed.
      }
    }
    this.setState(liveTelemetryEnabled() ? 'disconnected' : 'disabled');
  }

  /** Bind this connection to one project. Bearer-authenticated. */
  private async subscribe(
    projectName: string,
    connectionId: string
  ): Promise<boolean> {
    if (!connectionId) {
      console.warn(
        'LiveTelemetry: no connectionId after start() — cannot subscribe'
      );
      return false;
    }
    return this.subscription(
      liveTelemetryConfig().subscribeRoute,
      projectName,
      connectionId
    );
  }

  /** Release the binding. Failure is logged, never thrown: we are tearing down. */
  private async unsubscribe(
    projectName: string,
    connectionId: string
  ): Promise<boolean> {
    return this.subscription(
      liveTelemetryConfig().unsubscribeRoute,
      projectName,
      connectionId
    );
  }

  /**
   * POST a subscription change.
   *
   * Goes through Angular's HttpClient so VericureAuthInterceptor attaches the
   * VeriCure bearer — which is the whole basis on which the server resolves the
   * caller's company and decides whether this connection may see this project.
   */
  private async subscription(
    route: string,
    projectName: string,
    connectionId: string
  ): Promise<boolean> {
    const base = environment.apiUrl.endsWith('/')
      ? environment.apiUrl
      : `${environment.apiUrl}/`;
    const path = route.replace(/^\/+/, '');
    const url =
      `${base}${path}?projectName=${encodeURIComponent(projectName)}` +
      `&connectionId=${encodeURIComponent(connectionId)}`;
    try {
      // ECHO THE GROUP THE SERVER ACTUALLY JOINED US TO.
      //
      // The response body was discarded, so a verification could confirm that
      // subscribe returned OK and nothing more — and "OK but no frames" is
      // precisely the state that has now been observed twice. The server logs
      // `LiveAdd: project=... group=p-... connectionId=...` on its side; logging
      // the echoed group and the connectionId here makes the two directly
      // diffable from a single browser capture, instead of relaying hashes by
      // hand across two systems.
      //
      // Tolerant of the field being absent: an older server that does not echo
      // it must not turn a successful subscribe into a failed one.
      const body = (await firstValueFrom(this.http.post(url, {}))) as
        | { group?: string }
        | null;
      const group = body?.group ?? '(not echoed)';
      this.lastGroup = group;
      this.lastConnectionId = connectionId;
      console.log(
        `LiveTelemetry: ${path} ok for '${projectName}' ` +
          `group=${group} connectionId=${connectionId}`
      );
      return true;
    } catch (err) {
      const e = err as { status?: number; message?: string };
      console.warn(
        `LiveTelemetry: ${path} FAILED for '${projectName}' — ` +
          `status=${e?.status ?? '?'} ${e?.message ?? ''}`
      );
      return false;
    }
  }

  /** Group the server reported joining, and the connection it bound. */
  private lastGroup = '';
  private lastConnectionId = '';

  /** Re-seed one family after its catch-up poll. */
  seed(family: TelemetryFamilyName, epoch: number): void {
    this.sequencers[family].seed(epoch);
  }

  stats() {
    return {
      state: this.stateSource.value,
      project: this.projectName,
      foreignTicks: this.foreignTicks,
      lastGroup: this.lastGroup,
      lastConnectionId: this.lastConnectionId,
      lastFamiliesSeen: this.lastFamiliesSeen,
      families: {
        TEMPERATURE: this.familyStats('TEMPERATURE'),
        ZONESTORAGE: this.familyStats('ZONESTORAGE'),
        LOSS: this.familyStats('LOSS'),
      },
    };
  }

  /**
   * Handle one broadcast tick.
   *
   * The stream is GLOBAL, so the project filter is a correctness requirement:
   * without it this client would ingest other projects' telemetry. Note the field
   * is `project`, not `projectName`.
   */
  /** Last sequence number seen per family, for gap detection. */
  private lastSeq = new Map<string, number>();
  /** Ticks received for a project other than the open one. Should be 0. */
  private foreignTicks = 0;
  private warnedForeign = false;
  /** Families already reported as shape-mismatched; one warning each. */
  private warnedShape = new Set<string>();

  /** Families seen in the last envelope, for cross-checking against the publisher. */
  private lastFamiliesSeen: string[] = [];

  /**
   * One broadcast may carry SEVERAL families. Split it, then ingest each.
   *
   * The top-level family/data pair is deliberately ignored whenever `families`
   * is present: since coalescing it repeats one of the entries rather than
   * adding a fourth, so ingesting both would double that family's rows.
   */
  onTick(message: LiveTickMessage | null | undefined): void {
    // SEQUENCE IS PER ENVELOPE, NOT PER FAMILY.
    //
    // Observed on the wire: seq 323 carried {LOSS, TEMPERATURE, ZONESTORAGE},
    // 324 carried {TEMPERATURE} alone, 325 carried {LOSS, ZONESTORAGE}. Keyed by
    // family, LOSS reads 323 -> 325 and reports a gap that never happened — and
    // every false gap costs a catch-up poll. A missing seq means a lost MESSAGE,
    // which may have carried any families, so the check belongs here, once.
    this.checkEnvelopeSequence(message);
    for (const frame of this.framesOf(message)) {
      this.ingestFrame(frame);
    }
  }

  /** Last seq seen per SCOPE — see sequenceScope for why that is not fixed. */
  private lastSeqByScope = new Map<string, number>();

  /**
   * WHAT seq COUNTS DEPENDS ON WHETHER THE SERVER IS COALESCING.
   *
   * Confirmed against the publisher: the counter key is
   *
   *     coalesce ? projectName : projectName + "|" + DataType
   *
   * so with coalescing ON — the deployed default — seq increments once per
   * ENVELOPE however many families ride on it, and with coalescing OFF it
   * increments per family. Tracking it the wrong way round invents gaps in both
   * directions: per family under coalescing (a family absent from one envelope
   * looks like a hole), and per envelope without it (interleaved per-family
   * counters look like jumps).
   *
   * The shape of the frame tells us which regime we are in, so the scope is
   * derived rather than configured — no flag to keep in sync with the server.
   */
  private sequenceScope(message: LiveTickMessage): string {
    return this.familyEntries(message).length > 0
      ? 'envelope'
      : `family:${message.family ?? '?'}`;
  }

  private checkEnvelopeSequence(
    message: LiveTickMessage | null | undefined
  ): void {
    const seq = message?.seq;
    if (!message || typeof seq !== 'number') {
      return;
    }
    const scope = this.sequenceScope(message);
    const last = this.lastSeqByScope.get(scope);
    if (last !== undefined && seq > last + 1) {
      console.warn(
        `LiveTelemetry: ${scope} sequence gap ${last} -> ${seq}; ` +
          `requesting one catch-up read`
      );
      this.catchUpSource.next(`${scope} sequence gap at ${seq}`);
    }
    if (last === undefined || seq > last) {
      this.lastSeqByScope.set(scope, seq);
    }
  }

  private framesOf(
    message: LiveTickMessage | null | undefined
  ): LiveTickMessage[] {
    if (!message) {
      return [];
    }

    const entries = this.familyEntries(message);
    if (entries.length === 0) {
      // Genuinely single-family: no families payload at all. Top-level is then
      // the whole tick and reading it is correct.
      this.lastFamiliesSeen = [message.family ?? '?'];
      console.log(
        `LIVE-FRAME single family=[${message.family ?? '?'}] ts=${message.timestamp}`
      );
      return [message];
    }

    const names = entries.map(([family]) => family);
    this.lastFamiliesSeen = names;
    console.log(
      `LIVE-FRAME families=[${names.join(', ')}] ts=${message.timestamp} seq=${message.seq}`
    );

    // The entries carry only { ds, data }; everything else is the envelope's.
    // seq is deliberately NOT forwarded — ordering is per family by EPOCH, and
    // the envelope sequence is checked once in onTick.
    return entries.map(([family, frame]) => ({
      project: message.project,
      family,
      timestamp: frame?.timestamp ?? message.timestamp,
      data: frame?.data,
      hwm: message.hwm,
    }));
  }

  /**
   * The families on this envelope, as [name, frame] pairs.
   *
   * The server sends an OBJECT keyed by family name. An array is tolerated so a
   * contract change in that direction degrades rather than blinds us — that
   * asymmetry is deliberate: assuming the array shape is exactly what silently
   * dropped every non-LOSS family on ~4 in 10 frames, because the array check
   * failed and the code fell through to the top-level pair, which is always the
   * ordinal-first family.
   */
  private familyEntries(
    message: LiveTickMessage
  ): Array<[string, LiveFamilyFrame | undefined]> {
    const families = message.families;
    if (!families) {
      return [];
    }
    if (Array.isArray(families)) {
      return families
        .filter((f) => !!f?.family)
        .map((f) => [f.family as string, f]);
    }
    if (typeof families === 'object') {
      return Object.keys(families).map((name) => [name, families[name]]);
    }
    return [];
  }

  private ingestFrame(message: LiveTickMessage | null | undefined): void {
    const tick = this.validate(message);
    if (!tick) {
      return;
    }
    if (this.projectName && tick.project !== this.projectName) {
      // A tick for someone else's project reaching this browser AT ALL is the
      // exposure being closed. The filter keeps it out of the charts, but it
      // cannot un-receive it — so count it, and say so once. Silence here would
      // make server-side group routing impossible to verify from the client:
      // zero foreign ticks is the evidence that it works.
      this.foreignTicks++;
      if (!this.warnedForeign) {
        this.warnedForeign = true;
        console.warn(
          `LiveTelemetry: received a tick for '${tick.project}' while open on ` +
            `'${this.projectName}' — cross-project traffic is still reaching ` +
            `this client. Dropped; see stats().foreignTicks for the running count.`
        );
      }
      return;
    }
    // Gap detection lives on the ENVELOPE (see checkEnvelopeSequence): seq
    // counts messages, not per-family ticks, so a family absent from one
    // envelope is not a gap in that family.

    const released = this.sequencers[tick.family].offer(tick);
    if (released.length > 0) {
      this.emit(released);
    }
  }

  /** Validate and normalise a broadcast payload; null when unusable. */
  private validate(message: LiveTickMessage | null | undefined): LiveTick | null {
    if (!message) {
      return null;
    }
    const timestamp = Number(message.timestamp);
    if (!Number.isFinite(timestamp) || timestamp <= 0) {
      return null;
    }
    if (!isKnownFamily(message.family)) {
      // An unrecognised family is not an error — the server may add more later,
      // and ignoring them is the forward-compatible behaviour.
      return null;
    }
    if (!Array.isArray(message.data)) {
      // A FRAME WE CANNOT USE MUST NOT VANISH QUIETLY.
      //
      // The client is push-first: it renders from this payload. A server frame
      // whose rows live under a different key is therefore dropped here and
      // nothing downstream ever knows — the chart simply stops advancing, which
      // looks like the socket being idle rather than a shape mismatch. Reported
      // once per family so a contract disagreement is visible immediately
      // without a wall of identical lines.
      if (!this.warnedShape.has(message.family)) {
        this.warnedShape.add(message.family);
        console.warn(
          `LiveTelemetry: DROPPING ${message.family} frame — no 'data' array. ` +
            `Keys received: [${Object.keys(message).join(', ')}]. The client ` +
            `renders from the pushed payload, so this frame contributes nothing.`
        );
      }
      return null;
    }
    return {
      project: message.project ?? '',
      family: message.family,
      timestamp,
      data: message.data,
      seq: typeof message.seq === 'number' ? message.seq : undefined,
      hwm: typeof message.hwm === 'number' ? message.hwm : undefined,
      E: timestamp,
    };
  }

  private resetSequencers(
    seed: Partial<Record<TelemetryFamilyName, number>>
  ): void {
    for (const family of Object.keys(this.sequencers) as TelemetryFamilyName[]) {
      this.sequencers[family].reset();
      const epoch = seed[family];
      if (typeof epoch === 'number') {
        this.sequencers[family].seed(epoch);
      }
    }
  }

  private familyStats(family: TelemetryFamilyName) {
    const s = this.sequencers[family];
    return {
      highWaterMark: s.highWaterMark,
      duplicatesDropped: s.duplicatesDropped,
      released: s.totalReleased,
    };
  }

  private emit(ticks: LiveTick[]): void {
    // SignalR callbacks land outside Angular's zone; re-enter so charts update.
    this.zone.run(() => this.ticksSource.next(ticks));
  }

  private setState(state: LiveConnectionState): void {
    if (this.stateSource.value !== state) {
      this.zone.run(() => this.stateSource.next(state));
    }
  }
}
